<!--
  «Tipos y flujos» — la portada. Cuatro renglones, uno debajo del otro, a ancho completo.

  El workflow se casa al TIPO de solicitud, no al comercio (corrección de Juan, 2026-09-20), así
  que aquí no hay nada por comercio: ni carriles, ni columnas, ni el filtro de la cabecera.

  Y no hay dibujo: tres de los cuatro tipos tienen un paso y el cuarto tiene dos. Lo que un
  dibujo contaba —que tres hacen lo mismo— lo dice la frase de arriba, y se calcula con los datos.
-->
<div class="kb-page kb-gateway">

  <!-- ══ Portada ══════════════════════════════════════════════════════════════════════════════
       El renglón de abajo tiene que servir SÓLO para esta pantalla: si sirve para otras tres,
       está mal escrito. -->
  <div class="kb-page-header">
    <div>
      <h1 class="kb-page-title flex items-center gap-2">
        <i class="fas fa-project-diagram text-[0.85em] text-muted-foreground" aria-hidden="true"></i>Tipos y pasos del cliente
      </h1>
      <p class="kb-page-meta">
        Las formas de entrar una venta al canal POS, y qué pasos le pide al cliente cada una.
      </p>
    </div>
    <button type="button" class="kb-btn kb-btn-outline kb-btn-sm" (click)="cargar()" [disabled]="cargando">
      <i class="fas fa-sync-alt" aria-hidden="true"></i>Volver a consultar el catálogo
    </button>
  </div>

  <!-- ══ Cargando ══════════════════════════════════════════════════════════════════════════════ -->
  <div class="space-y-6" *ngIf="cargando" aria-hidden="true">
    <div class="kb-skeleton h-8 w-2/3"></div>
    <div class="kb-skeleton h-56 w-full"></div>
  </div>
  <p class="sr-only" *ngIf="cargando">Cargando el catálogo de tipos y flujos.</p>

  <!-- ══ Error ═════════════════════════════════════════════════════════════════════════════════ -->
  <div class="kb-aviso kb-aviso-peligro" *ngIf="!cargando && error">
    <i class="fas fa-exclamation-circle mt-0.5" aria-hidden="true"></i>
    <div>
      <p class="kb-aviso-titulo">No se pudo traer el catálogo</p>
      <p>{{ error }}</p>
      <button type="button" class="kb-btn kb-btn-outline kb-btn-sm mt-3" (click)="cargar()">
        <i class="fas fa-redo" aria-hidden="true"></i>Volver a consultar el catálogo
      </button>
    </div>
  </div>

  <ng-container *ngIf="!cargando && !error">

    <!-- ══ La conclusión ═══════════════════════════════════════════════════════════════════════
         Lo que alguien viene a entender aquí, en una frase — calculada con los datos del catálogo,
         no escrita a mano: el día que un tipo cambie de recorrido, la frase cambia sola. Una
         conclusión escrita a mano es una que miente sin avisar. -->
    <p class="tf-conclusion" *ngIf="frase">{{ frase }}</p>

    <!-- ══ Los tipos, uno debajo del otro ══════════════════════════════════════════════════════ -->
    <ol class="tf-lista" *ngIf="carriles.length">
      <li class="tf-fila" *ngFor="let c of carriles">

        <div class="tf-fila-cabeza">
          <a class="tf-nombre" [routerLink]="['/console/tipos-flujos', c.tipo.code]">
            {{ c.tipo.nombre }}<i class="fas fa-chevron-right" aria-hidden="true"></i>
          </a>
          <span class="kb-badge kb-badge-marca" *ngIf="c.tipo.por_omision">por omisión</span>
          <span class="kb-badge kb-badge-contorno tf-codigo">{{ c.tipo.code }}</span>
        </div>

        <p class="tf-origen">{{ c.tipo.origen }}</p>

        <!-- El recorrido: una línea de pasos. Si son dos, se ve la flecha; si es uno, se ve uno.
             Sin huecos y sin marcas de lo que ese tipo no hace. -->
        <ol class="tf-recorrido" *ngIf="c.nodos.length; else sinPasos">
          <ng-container *ngFor="let n of c.nodos; let primero = first">
            <li class="tf-flecha" *ngIf="!primero" aria-hidden="true">
              <i class="fas fa-long-arrow-alt-right"></i>
            </li>
            <li class="tf-pastilla" [ngClass]="'tf-pastilla-' + n.decide.toLowerCase()">
              <span class="tf-pastilla-orden" aria-hidden="true">{{ n.orden }}</span>
              <span class="tf-pastilla-nombre">{{ n.nombre }}</span>
              <!-- El `required` del catálogo. Se dice SÓLO cuando el paso es opcional: hoy los
                   cuatro recorridos son obligatorios de punta a punta, y repetir «obligatorio» en
                   cada pastilla sería una palabra que no distingue a nadie de nadie. El detalle sí
                   lo dice siempre, que es donde se va a leer qué es cada paso. -->
              <span class="tf-pastilla-quien">{{ textoDecideCorto(n.decide) }}<ng-container
                *ngIf="n.requerido === false"> &middot; opcional</ng-container></span>
            </li>
          </ng-container>
        </ol>

        <ng-template #sinPasos>
          <p class="tf-sin-pasos">Hoy este tipo no le pide ningún paso al cliente.</p>
        </ng-template>
      </li>
    </ol>

    <!-- ══ Vacío ═══════════════════════════════════════════════════════════════════════════════ -->
    <div class="kb-tarjeta" *ngIf="!carriles.length">
      <div class="kb-empty">
        <div class="kb-empty-icono"><i class="fas fa-project-diagram" aria-hidden="true"></i></div>
        <p class="kb-empty-titulo">El catálogo no trae ningún tipo de solicitud</p>
        <p class="kb-empty-texto">
          Los tipos nacen en el código del canal POS, no en una configuración: si aquí no hay
          ninguno, el catálogo del servidor volvió vacío.
        </p>
      </div>
    </div>

    <!-- ══ Las notas del pie ═══════════════════════════════════════════════════════════════════
         Sin caja y sin icono de alerta: son notas, no avisos. -->
    <div class="tf-notas" *ngIf="carriles.length">
      <p *ngIf="lineaApagados">{{ lineaApagados }}</p>

      <!-- El control: el workflow es del tipo, pero la versión propia de un comercio sigue
           existiendo en la base. Hoy ninguna cambia un recorrido, así que esta línea no se ve; el
           día que alguna lo cambie, aparece sola. -->
      <p class="tf-nota-alerta" *ngIf="comerciosQueCambian.length">
        <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
        {{ comerciosQueCambian.length }}
        {{ comerciosQueCambian.length === 1 ? 'comercio tiene' : 'comercios tienen' }} una versión
        propia del flujo que cambia estos recorridos:
        <span *ngFor="let m of comerciosQueCambian; let ultimo = last">{{ m.nombre || m.merchant_code }}<span *ngIf="!ultimo">, </span></span>.
      </p>

      <p class="tf-vigencia-linea">
        <i class="fas fa-history" aria-hidden="true"></i>
        <span>Así está configurado <strong>hoy, {{ hoy }}</strong>.</span>
        <button type="button" class="tf-vigencia-boton"
                [attr.aria-expanded]="vigenciaAbierta"
                (click)="vigenciaAbierta = !vigenciaAbierta">
          Por qué importa
          <i class="fas" [ngClass]="vigenciaAbierta ? 'fa-chevron-up' : 'fa-chevron-down'" aria-hidden="true"></i>
        </button>
      </p>
      <p class="tf-vigencia-texto" *ngIf="vigenciaAbierta">
        Cada solicitud guarda la fotografía del flujo que le tocó el día en que se creó
        (<code class="kb-td-mono">flow_snapshot</code>). Si estás explicando una solicitud
        <strong>vieja</strong>, pudo haber seguido otro recorrido — no necesariamente el que ves aquí.
      </p>
    </div>

  </ng-container>
</div>
